import { Transform } from 'class-transformer';
import {
  IsBoolean,
  IsIn,
  IsNumber,
  IsOptional,
  IsString,
  IsUUID,
  Max,
  MaxLength,
  Min,
} from 'class-validator';
import type { CitizenMilestoneDto, CitizenMilestoneType } from './citizen-portal.dto';
import {
  formBool,
  SubmitCitizenComplaintDto,
  type DeclinePublicReason,
} from './citizen-complaint.dto';
import type { BylawCategory } from './files.dto';

/**
 * Citizen reporting — the 311-style channel a municipality can switch on.
 *
 * It is the public complaint form grown up: the same "a report creates a
 * complaint awaiting triage and nothing else" rule (see citizen-complaint.dto),
 * plus what a resident needs to report well and hear back — a category, photos,
 * a pin on the map, a tracking link, and email updates they opted into.
 *
 * Off by default per municipality (`local_government.citizen_reporting_enabled`).
 * Where it is off the plain form keeps working exactly as before.
 */

/**
 * What a resident can say the problem is.
 *
 * Phrased the way a resident sees it, not the way the by-law is organised —
 * nobody outside the municipality knows that a pile of tires is "unsightly
 * premises". Each maps to the file's by-law category when an officer opens a
 * file from it ({@link bylawCategoryForReport}); the officer can change it.
 */
export const CITIZEN_REPORT_CATEGORIES = [
  'unsightly_property',
  'dangerous_building',
  'vacant_unsecured',
  'overgrown_vegetation',
  'garbage_debris',
  'abandoned_vehicle',
  'parking',
  'other',
] as const;
export type CitizenReportCategory = (typeof CITIZEN_REPORT_CATEGORIES)[number];

export function isCitizenReportCategory(value: unknown): value is CitizenReportCategory {
  return (
    typeof value === 'string' && (CITIZEN_REPORT_CATEGORIES as readonly string[]).includes(value)
  );
}

/** The by-law category an officer's file starts from when opened from a report. */
export function bylawCategoryForReport(category: CitizenReportCategory | null): BylawCategory {
  if (category === 'parking') return 'parking';
  if (category === 'other' || category === null) return 'other';
  return 'dangerous_unsightly';
}

/**
 * The intake method a citizen report is recorded under.
 *
 * Its own key in the administrable `gov_intake_method` list (seeded by the
 * CitizenReporting migration) rather than the plain form's `written`: an
 * officer — and a court, later — should be able to tell a report typed by a
 * resident on the website apart from a letter.
 */
export const CITIZEN_REPORT_INTAKE_METHOD = 'online_report';

/** At most this many photos on one report. */
export const CITIZEN_REPORT_MAX_PHOTOS = 5;

/**
 * Photo types a resident may attach.
 *
 * Narrower than the image types accepted elsewhere because every photo is
 * re-written before it is stored — its EXIF block (camera serial, the exact
 * spot the resident stood, often their own house) removed. That is done for
 * JPEG and PNG; a format we cannot clean is a format we do not store. Browsers
 * and the app send JPEG regardless.
 */
export const CITIZEN_REPORT_PHOTO_TYPES = ['image/jpeg', 'image/png'] as const;

/**
 * How close two reports — or a report and a file — must be to be offered to
 * the officer as possibly the same thing. A city lot is ~20 m wide; 150 m
 * catches "the house across the road" and a pin dropped a little off without
 * pulling in the next street.
 */
export const CITIZEN_REPORT_DUPLICATE_RADIUS_M = 150;

/** How far back a report is still a plausible duplicate of a new one. */
export const CITIZEN_REPORT_DUPLICATE_WINDOW_DAYS = 90;

/**
 * Version of the consent wording the resident agreed to, stored beside the
 * consent itself. CASL asks that consent be provable; "they ticked a box" is
 * not proof unless we can say what the box said. Bump when the copy changes.
 */
export const CITIZEN_REPORT_CONSENT_VERSION = '2026-10';

/** Where the report's coordinates came from. */
export const CITIZEN_REPORT_LOCATION_SOURCES = ['pin', 'address', 'device', 'photo'] as const;
export type CitizenReportLocationSource = (typeof CITIZEN_REPORT_LOCATION_SOURCES)[number];

/** Multipart fields arrive as strings; see {@link formBool}. */
const toBool = formBool;

/** Blank means absent; anything else must parse as a finite number. */
const toNumber = ({ value }: { value: unknown }) => {
  if (value === undefined || value === null || value === '') return undefined;
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : value;
};

/**
 * What the citizen reporting form sends — multipart, with up to
 * {@link CITIZEN_REPORT_MAX_PHOTOS} `photos` parts alongside.
 *
 * Extends the plain form's DTO so every rule there (trimming, the description
 * floor, the anonymity semantics) holds here unchanged.
 */
export class SubmitCitizenReportDto extends SubmitCitizenComplaintDto {
  @IsIn(CITIZEN_REPORT_CATEGORIES)
  category!: CitizenReportCategory;

  @Transform(toNumber)
  @IsOptional()
  @IsNumber()
  @Min(-90)
  @Max(90)
  latitude?: number;

  @Transform(toNumber)
  @IsOptional()
  @IsNumber()
  @Min(-180)
  @Max(180)
  longitude?: number;

  @IsOptional()
  @IsIn(CITIZEN_REPORT_LOCATION_SOURCES)
  locationSource?: CitizenReportLocationSource;

  /**
   * "Email me updates about this report." Unticked by default — express,
   * opt-in consent — and meaningless without an email address.
   */
  @Transform(toBool)
  @IsOptional()
  @IsBoolean()
  updatesConsent?: boolean;
}

/** What the reporter gets back. Still nothing about the property. */
export interface CitizenReportReceipt {
  reference: string;
  /**
   * The secret in the tracking link. Shown once, here and in the
   * acknowledgement email; only its hash is stored, so it cannot be recovered
   * or listed — losing it means asking the municipality.
   */
  trackingToken: string;
  /** Path on the Client Portal the token opens, e.g. `/report/track/<token>`. */
  trackingPath: string;
  /** The same, as an absolute Client Portal URL — what the mobile app opens. */
  trackingUrl: string;
  acknowledgementSent: boolean;
}

/**
 * Where a report has got to, in the reporter's terms.
 *
 * `merged` is said as "added to an existing file" — the municipality was
 * already dealing with it — and is not a refusal.
 */
export type CitizenReportStatus = 'received' | 'file_opened' | 'merged' | 'declined';

/**
 * The tracking page. Built for the one person who holds the link, and still
 * deliberately thin: what they reported, what the municipality decided, and
 * the same public milestones the Client Portal shows an owner — dated facts,
 * never a name, a finding, or anything from inside the record.
 */
export interface CitizenReportTrackingView {
  reference: string;
  municipality: string;
  category: CitizenReportCategory | null;
  subjectAddress: string;
  submittedAt: string;
  status: CitizenReportStatus;
  decidedAt: string | null;
  declineReason: DeclinePublicReason | null;
  milestones: CitizenMilestoneDto[];
  photoCount: number;
  /** True while email updates are on; the page offers to turn them off. */
  updatesSubscribed: boolean;
  /**
   * Whether email is going out at all (the admin "Email sending" switch). False
   * means a subscribed reporter is not being emailed right now, and the page
   * must not say they are. Absent from older servers: read as on.
   */
  emailSending?: boolean;
  /**
   * What the reporter wrote in "What have you seen?". Only on a signed-in
   * resident's own report (`GET /citizen-reports/mine/:id`) — never on the
   * tracking-link view, whose link can be forwarded.
   */
  description?: string;
}

/** One row of "my reports" for a resident signed in to the Client Portal. */
export interface CitizenReportSummary {
  id: string;
  reference: string;
  category: CitizenReportCategory | null;
  subjectAddress: string;
  municipality: string;
  submittedAt: string;
  status: CitizenReportStatus;
  latestMilestone: CitizenMilestoneType | null;
}

/** A municipality on the report form, and whether it takes full reports. */
export interface ReportableGovernment {
  id: string;
  name: string;
  /** Category, photos, map and tracking — the citizen reporting module. */
  citizenReporting: boolean;
}

// --- Officer side ---------------------------------------------------------

/** Something near a report: another report, or a file already open. */
export interface NearbyMatch {
  kind: 'report' | 'file';
  id: string;
  reference: string;
  /** Rounded to the metre. */
  distanceM: number;
  /** Where it is, for drawing it beside the report's pin. */
  latitude: number;
  longitude: number;
  /** A report with the same category; a file is never "same category". */
  sameCategory: boolean;
  /** Triage status for a report, stage for a file. */
  status: string;
  createdAt: string;
}

export interface CitizenReportPhoto {
  id: string;
  contentType: string;
  /** Short-lived signed URL. */
  url: string;
  /** From the photo's own EXIF, before it was removed — null when it had none. */
  latitude: number | null;
  longitude: number | null;
  takenAt: string | null;
}

/** The citizen-reporting half of a triage item. */
export interface TriageCitizenReport {
  category: CitizenReportCategory | null;
  latitude: number | null;
  longitude: number | null;
  locationSource: CitizenReportLocationSource | null;
  photos: CitizenReportPhoto[];
  updatesConsent: boolean;
  /** Signed in to the Client Portal when they reported. */
  fromPortalAccount: boolean;
  /** Likely duplicates, nearest first. */
  nearby: NearbyMatch[];
}

/** `POST /triage/:id/merge` — fold a report into a file already open. */
export class MergeComplaintDto {
  @IsUUID()
  fileId!: string;

  /** For the file's activity log. Never shown to the reporter. */
  @IsOptional()
  @IsString()
  @MaxLength(2000)
  note?: string;
}
